import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  MessagingMapDraft,
  MessagingMapResponse,
  MessagingRollupResponse,
  MessagingScript,
  MessagingScriptDraft,
  MessagingScriptsResponse,
  StrategyCampaign
} from '../models/messaging-map.model';

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

/**
 * Messaging map API on the integrations service.
 *
 * GET /companies/{companyId}/strategy/map — any company member.
 * PUT /companies/{companyId}/strategy/map — needs manage_sequences (backend
 * enforces it). `expectedVersion` is null to create the first map, else the
 * version last read; a mismatch is a 409 with error 'MAP_VERSION_CONFLICT'.
 *
 * The feature is on only for allowlisted companies: GET answers
 * enabled: false for the rest, and PUT is a 403 FEATURE_NOT_ENABLED.
 */
@Injectable({
  providedIn: 'root'
})
export class MessagingMapService {
  private apiUrl = environment.baseUrl;
  /**
   * Recent enabled flag per company, so the nav doesn't refetch on every
   * switch. Short-lived so an allowlist change shows up without a reload;
   * errors are never cached.
   */
  private enabledByCompany = new Map<string, { value: boolean; at: number }>();
  private enabledInFlight = new Map<string, Promise<boolean>>();
  private static readonly ENABLED_TTL_MS = 5 * 60 * 1000;

  constructor(private http: HttpClient) {}

  async getMap(companyId: string): Promise<MessagingMapResponse> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<MessagingMapResponse>>(
        `${this.apiUrl}/companies/${encodeURIComponent(companyId)}/strategy/map`,
        { headers: this.getHeaders() }
      )
    );
    const result = this.toResult(response);
    this.setEnabled(companyId, result.enabled);
    return result;
  }

  /** Whether the map is switched on for a company. Errors count as off and are not cached. */
  async isEnabled(companyId: string): Promise<boolean> {
    const known = this.enabledByCompany.get(companyId);
    if (known && Date.now() - known.at < MessagingMapService.ENABLED_TTL_MS) {
      return known.value;
    }
    const pending = this.enabledInFlight.get(companyId);
    if (pending) {
      return pending;
    }
    const request = this.getMap(companyId)
      .then(result => result.enabled)
      .catch(() => false)
      .finally(() => this.enabledInFlight.delete(companyId));
    this.enabledInFlight.set(companyId, request);
    return request;
  }

  /** Record what the API just said, e.g. a FEATURE_NOT_ENABLED refusal. */
  setEnabled(companyId: string, enabled: boolean): void {
    this.enabledByCompany.set(companyId, { value: enabled, at: Date.now() });
  }

  async saveMap(companyId: string, map: MessagingMapDraft, expectedVersion: number | null): Promise<MessagingMapResponse> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<MessagingMapResponse>>(
        `${this.apiUrl}/companies/${encodeURIComponent(companyId)}/strategy/map`,
        { map, expectedVersion },
        { headers: this.getHeaders() }
      )
    );
    return this.toResult(response);
  }

  async getScripts(companyId: string): Promise<MessagingScriptsResponse> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<MessagingScriptsResponse>>(`${this.strategyUrl(companyId)}/scripts`, { headers: this.getHeaders() })
    );
    return { scripts: response.data?.scripts ?? [], canEdit: response.data?.canEdit === true };
  }

  /**
   * Create (expectedVersion null) or update a script. A version mismatch is a
   * 409 'SCRIPT_VERSION_CONFLICT'; a campaign already linked to another script
   * is a 409 'CAMPAIGN_ALREADY_LINKED'.
   */
  async saveScript(
    companyId: string,
    scriptId: string,
    script: MessagingScriptDraft,
    expectedVersion: number | null
  ): Promise<MessagingScript> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<{ script: MessagingScript }>>(
        `${this.strategyUrl(companyId)}/scripts/${encodeURIComponent(scriptId)}`,
        { script, expectedVersion },
        { headers: this.getHeaders() }
      )
    );
    if (!response.data?.script) {
      throw new Error('The script was not returned');
    }
    return response.data.script;
  }

  /** Deletes only at `expectedVersion`; a newer save is a 409 'SCRIPT_VERSION_CONFLICT'. */
  async deleteScript(companyId: string, scriptId: string, expectedVersion: number): Promise<void> {
    await firstValueFrom(
      this.http.delete<ApiResponse>(`${this.strategyUrl(companyId)}/scripts/${encodeURIComponent(scriptId)}`, {
        headers: this.getHeaders(),
        params: { expectedVersion: String(expectedVersion) }
      })
    );
  }

  /** Campaigns seen in the company's messages, most messages first. */
  async getCampaigns(companyId: string): Promise<StrategyCampaign[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<{ campaigns: StrategyCampaign[] }>>(`${this.strategyUrl(companyId)}/campaigns`, {
        headers: this.getHeaders()
      })
    );
    return response.data?.campaigns ?? [];
  }

  /** Observational felt need x seniority rollup, computed on request. */
  async getRollup(companyId: string): Promise<MessagingRollupResponse> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<MessagingRollupResponse>>(`${this.strategyUrl(companyId)}/rollup`, { headers: this.getHeaders() })
    );
    if (!response.data?.rollup) {
      throw new Error('The rollup was not returned');
    }
    return response.data;
  }

  private strategyUrl(companyId: string): string {
    return `${this.apiUrl}/companies/${encodeURIComponent(companyId)}/strategy`;
  }

  private toResult(response: ApiResponse<MessagingMapResponse>): MessagingMapResponse {
    return {
      enabled: response.data?.enabled === true,
      map: response.data?.map ?? null,
      canEdit: response.data?.canEdit === true
    };
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }
}
